import {AxiosError} from "axios";
import {useState} from 'react'
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {
    CAApi,
    CACertificateSignatureAlgorithm,
    CAControllerCreateCAIntermediateKeyLengthEnum,
    CAControllerCreateCARootKeyLengthEnum,
    X500PrincipalRequest
} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {X500Subject} from "./X500Subject";

/*
 * REST api for accessing the back-end
 */
const caApi = new CAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CreateCADialogProps {
    onClose : OnClose
}

export const CreateCADialog = ({onClose} : CreateCADialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Root Certificate subject
     */
    const [rootSubject, setRootSubject] = useState<X500PrincipalRequest>({commonName : ""});
    /**
     * Intermediate Certificate subject
     */
    const [intermediateSubject, setIntermediateSubject] = useState<X500PrincipalRequest>({commonName : ""});
    /**
     * Root key length
     */
    const [rootKeyLength, setRootKeyLength] = useState<number>(3072);
    /**
     * Intermediate key length
     */
    const [intermediateKeyLength, setIntermediateKeyLength] = useState<number>(3072);
    /**
     * Root validity
     */
    const [rootValidity, setRootValidity] = useState<number>(3650);
    /**
     * Intermediate validity
     */
    const [intermediateValidity, setIntermediateValidity] = useState<number>(3650);
    /**
     * Signature algorithm
     */
    const [signatureAlgorithm, setSignatureAlgorithm] = useState<CACertificateSignatureAlgorithm>(CACertificateSignatureAlgorithm.Sha256WithRsaEncryption);
    /**
     * Set to true while creating the certificates to show a spinner on the import button
     */
    const [creating, setCreating] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const createCACertificates = () =>
    {
        setCreating(true);

        caApi.cAControllerCreateCA(
                    rootValidity,
                    intermediateValidity,
                    rootKeyLength as CAControllerCreateCARootKeyLengthEnum,
                    intermediateKeyLength as CAControllerCreateCAIntermediateKeyLengthEnum,
                    signatureAlgorithm, {rootSubject, intermediateSubject})
            .then(() =>
            {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error creating CA certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setCreating(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("ca.create-root-and")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        <Form>
                            <Row>
                                <Col>
                                    <X500Subject header={t("ca.root-subject")}
                                                 subject={rootSubject}
                                                 setSubject={setRootSubject}
                                                 keyLength={rootKeyLength}
                                                 setKeyLength={setRootKeyLength}
                                                 validity={rootValidity}
                                                 setValidity={setRootValidity}
                                    />
                                </Col>
                                <Col>
                                    <X500Subject header={t("ca.intermediate-subject")}
                                                 subject={intermediateSubject}
                                                 setSubject={setIntermediateSubject}
                                                 keyLength={intermediateKeyLength}
                                                 setKeyLength={setIntermediateKeyLength}
                                                 validity={intermediateValidity}
                                                 setValidity={setIntermediateValidity}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mt-3">
                                        <Form.Label>{t("ca.signature-algorithm")}</Form.Label>
                                        <Form.Select value={signatureAlgorithm}
                                                     onChange={(e) => setSignatureAlgorithm(Utils.stringToEnum(CACertificateSignatureAlgorithm, e.target.value as CACertificateSignatureAlgorithm))}>
                                            <option value="SHA256_WITH_RSA_ENCRYPTION">SHA256WithRsa</option>
                                            <option value="SHA384_WITH_RSA_ENCRYPTION">SHA384WithRsa</option>
                                            <option value="SHA512_WITH_RSA_ENCRYPTION">SHA512WithRsa</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={createCACertificates}
                        disabled={StringUtils.isBlank(rootSubject.commonName) || StringUtils.isBlank(intermediateSubject.commonName)}>
                    {creating && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {creating ? t("common.creating") : t("common.create")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
